import { displayLabel } from "../../utils/displayLabel";
import React, { useEffect, useMemo, useState } from "react";
import { fetchUserAttributes } from "@aws-amplify/auth";
import { resolveSessionForType } from "../../auth/resolveSession";
import { homeStats } from "../../utils/homeStats";
import { fetchAuthSession } from "@aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import backgroundImage from "../../assets/LadyLiza1Fullbody.png";
import AuthorFollowerCount from "./AuthorFollowerCount";
import AuthorGenreCounts from "./AuthorGenreCounts";
const API_GET_AUTHOR_ORDERS =
  process.env.REACT_APP_GET_AUTHOR_ORDERS_API_URL ||
  "https://o3c9kbex70.execute-api.us-west-2.amazonaws.com/default/get-author-orders";

/* ───────────────────────────────────────────── */
/* Circle Stat                                  */
/* ───────────────────────────────────────────── */
const CircleStat = ({
  value,
  label,
  sublabel,
  ringClass = "border-bookish-purple",
  size = "md",
  children,
}) => {
  const sizeMap = {
    sm: "h-[80px] w-[80px] sm:h-[110px] sm:w-[110px]",
    md: "h-[100px] w-[100px] sm:h-[140px] sm:w-[140px]",
    lg: "h-[120px] w-[120px] sm:h-[170px] sm:w-[170px]",
  };

  const textMap = {
    sm: "text-xl sm:text-2xl",
    md: "text-2xl sm:text-3xl",
    lg: "text-3xl sm:text-4xl",
  };

  return (
    <div className="flex flex-col items-center text-center">
      <div
        className={`${sizeMap[size]} rounded-full border-4 bg-white/80 flex items-center justify-center shadow-sm ${ringClass}`}
      >
        <div className="space-y-1 px-3">
          {children ? (
            children
          ) : (
            <>
              <div className={`${textMap[size]} font-bold text-bookish-purple`}>
                {value}
              </div>
              {label && (
                <div className="text-xs text-bookish-purpleAccent">
                  {label}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {sublabel && (
        <div className="mt-3 max-w-[200px] text-sm text-bookish-purpleAccent">
          {sublabel}
        </div>
      )}
    </div>
  );
};

/* ───────────────────────────────────────────── */
/* Home                                         */
/* ───────────────────────────────────────────── */
const Home = () => {
  const navigate = useNavigate();

  const [authorData, setAuthorData] = useState({
    firstName: "",
    lastName: "",
    penName: "",
    email: "",
    subdomain: "",
  });

  const [loading, setLoading] = useState(true);

  const [profileError, setProfileError] = useState("");
  const [profileRetry, setProfileRetry] = useState(0);
  const [salesRetry, setSalesRetry] = useState(0);

  const [ordersToday, setOrdersToday] = useState(0);
  const [salesTotals, setSalesTotals] = useState({ today: 0, week: 0, month: 0 });
  const [salesLoading, setSalesLoading] = useState(true);
  const [salesError, setSalesError] = useState("");

  const [period, setPeriod] = useState("week");

  useEffect(() => {
    let mounted = true;
    const loadUser = async () => {
      setLoading(true); setProfileError("");
      try {
        const { data: attributes } = await resolveSessionForType("author", { restore: true, read: fetchUserAttributes });
        if (!mounted) return;

        setAuthorData({
          firstName: attributes["custom:firstname"] || "",
          lastName: attributes["custom:lastname"] || "",
          penName:
            attributes["custom:penname"] ||
            attributes["name"] ||
            "Author",
          email: attributes["custom:authoremail"] || attributes["email"] || "",
          subdomain:
            attributes["custom:subdomain"] ||
            attributes["custom:authorsubdomain"] ||
            attributes["custom:domain"] ||
            "",
        });
      } catch (err) {
        if (mounted) setProfileError("Your profile is unavailable. Retry, or return to Author Login if your session has ended.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadUser();
    return () => { mounted = false; };
  }, [profileRetry]);

  useEffect(() => {
    let mounted = true;

    const loadSales = async () => {
      try {
        setSalesLoading(true);
        setSalesError("");

        const { data: session } = await resolveSessionForType("author", { restore: true, read: fetchAuthSession });
        const idToken = session?.tokens?.idToken?.toString?.() || "";
        if (!idToken) throw new Error("Missing auth token");

        const response = await fetch(API_GET_AUTHOR_ORDERS, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (!response.ok) throw new Error(`Sales request failed (${response.status})`);

        const payload = await response.json();
        const stats = homeStats(payload);
        if (mounted) {
          setOrdersToday(stats.ordersToday);
          setSalesTotals(stats.totals);
        }
      } catch (err) {

        if (mounted) setSalesError("Sales are unavailable. Please retry.");
      } finally {
        if (mounted) setSalesLoading(false);
      }
    };

    loadSales();
    return () => {
      mounted = false;
    };
  }, [salesRetry]);

  const periodLabel = useMemo(
    () => period.charAt(0).toUpperCase() + period.slice(1),
    [period]
  );

  const formattedSalesTotal = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(salesTotals[period]),
    [period, salesTotals]
  );

  const openOrders = () => navigate("/authorportal/mainpage?tab=orders");

  if (loading) {
    return (
      <p role="status" className="text-center text-bookish-purple">
        Loading author data...
      </p>
    );
  }

  return (
    <div className="w-full text-bookish-purple">
      <div className="flex flex-col items-center text-center">
        <h2 className="text-4xl font-serif font-bold">
          Your Circles are growing!
        </h2>
      </div>

      {/* STATS */}
      <div className="mt-10">
        <div className="flex flex-col xl:flex-row flex-wrap items-center justify-center gap-10 xl:gap-16">
          <CircleStat
            size="lg"
            sublabel={
              <div className="flex gap-2 justify-center">
                <button type="button" aria-pressed={period === "today"} onClick={() => setPeriod("today")} className={"min-h-[44px] px-2 rounded-lg " + (period === "today" ? "bg-bookish-purple text-white" : "underline")}>
                  Today
                </button>
                |
                <button type="button" aria-pressed={period === "week"} onClick={() => setPeriod("week")} className={"min-h-[44px] px-2 rounded-lg " + (period === "week" ? "bg-bookish-purple text-white" : "underline")}>
                  Week
                </button>
                |
                <button type="button" aria-pressed={period === "month"} onClick={() => setPeriod("month")} className={"min-h-[44px] px-2 rounded-lg " + (period === "month" ? "bg-bookish-purple text-white" : "underline")}>
                  Month
                </button>
              </div>
            }
          >
            <button
              type="button"
              onClick={openOrders}
              className="rounded-md transition hover:opacity-75 focus:outline-none focus:ring-2 focus:ring-bookish-royal/40"
              aria-label="View all orders"
            >
              <div className="text-xl sm:text-3xl break-all font-bold text-bookish-purple">
                {salesLoading || salesError ? "—" : formattedSalesTotal}
              </div>
              <div className="text-xs text-bookish-purpleAccent">
                {period === "today" ? "Sales Today" : `Sales This ${periodLabel}`}
              </div>
            </button>
          </CircleStat>

          <CircleStat size="md" ringClass="border-bookish-royal" sublabel="Orders Today">
            <button
              type="button"
              onClick={openOrders}
              className="rounded-md text-2xl sm:text-3xl font-bold text-bookish-purple transition hover:opacity-75 focus:outline-none focus:ring-2 focus:ring-bookish-royal/40"
              title={salesError || undefined}
              aria-label="View all orders from today’s order count"
            >
              {salesLoading || salesError ? "—" : ordersToday}
            </button>
          </CircleStat>

          <CircleStat
            size="sm"
            sublabel="Readers who’ve joined your Circle"
          >
            <AuthorFollowerCount />
          </CircleStat>

          <div className="hidden xl:flex items-end">
            <img
              src={backgroundImage}
              alt="Lady Liza"
              className="h-48 sm:h-64 md:h-[360px] translate-y-6"
            />
          </div>
        </div>
      </div>

      <div className="mt-5 text-center text-sm text-bookish-purpleAccent">
        <p>Book sales include item prices and quantities for paid orders, before fees and refunds. Shipping and tax are excluded.</p>
        <p>Periods use your local time; weeks begin Sunday. Sales circles open all orders.</p>
        {salesLoading && <p role="status">Loading sales…</p>}
        {salesError && <p role="alert">{salesError}</p>}
        <button type="button" disabled={salesLoading} onClick={() => setSalesRetry(value => value + 1)} className="min-h-[44px] underline px-3 disabled:opacity-50">{salesError ? 'Retry sales' : 'Refresh sales'}</button>
      </div>
      {/* BOTTOM CARDS */}
      <div className="mt-6 px-0 sm:px-6">
        <div className="flex flex-col xl:flex-row gap-6 xl:justify-between">
          {/* GENRE CARD */}
          <div className="rounded-xl border border-bookish-purple/10 bg-bookish-lightCream p-4 shadow-sm w-full xl:max-w-[520px]">
            <div className="flex justify-between items-center">
              <h3 className="text-medium font-bold">Your Genre Circles</h3>
            </div>

            <AuthorGenreCounts />
          </div>

          {/* PROFILE CARD — RESTORED */}
          <div className="rounded-xl border border-bookish-purple/10 bg-bookish-lightCream p-4 shadow-sm w-full xl:max-w-[520px]">
            <div className="flex justify-between items-center mt-1">
              <h3 className="text-medium font-bold">Your Profile</h3>
              <button type="button" onClick={() => navigate("/authorportal/mainpage?tab=settings")} className="min-h-[44px] underline px-3">Open Settings</button>
            </div>

            {profileError ? <div role="alert"><p>{profileError}</p><button type="button" onClick={() => setProfileRetry(value => value + 1)} className="min-h-[44px] underline">Retry profile</button></div> : <div className="mt-3 space-y-2 text-[15px] break-words">
              <div>
                <span className="font-semibold">Pen Name:</span>{" "}
                {displayLabel(authorData.penName || "—")}
              </div>
              <div>
                <span className="font-semibold">Subdomain:</span>{" "}
                {authorData.subdomain || "—"}
              </div>
              <div>
                <span className="font-semibold">Email:</span>{" "}
                {authorData.email || "—"}
              </div>
            </div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
